import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import type { TrackPresenter } from '../level/TrackPresenter';
import type { EntityVisual } from '../rendering/EntityVisual';
import type { VisualPool } from '../rendering/VisualPool';
import type { ObstacleInstance } from './ObstacleInstance';

/** Attaches pooled visuals to obstacle instances. Visual ≠ collider: colliders live in ObstacleDefinition. */
export class ObstaclePresenter {
  private readonly visuals = new Map<number, EntityVisual>();

  constructor(
    private readonly pool: VisualPool,
    private readonly track: TrackPresenter,
    events: EventBus<GameEvents>,
  ) {
    events.on('obstacle:spawned', ({ obstacle }) => this.show(obstacle));
    events.on('obstacle:despawned', ({ obstacle }) => this.hide(obstacle));
    events.on('obstacle:cleared', ({ obstacle }) => this.hide(obstacle));
  }

  /** Visual of an obstacle, or undefined once it was cleared/despawned. */
  visualOf(obstacleUid: number): EntityVisual | undefined {
    return this.visuals.get(obstacleUid);
  }

  private show(obstacle: ObstacleInstance): void {
    const visual = this.pool.acquire(obstacle.definition.visualAssetId, this.track.rootOf(obstacle.segment));
    // Socket data is in path coordinates – on curves the visual follows the arc.
    const p = obstacle.segment.placement(obstacle.spawnPoint.position);
    visual.root.position.set(p.x, p.y, p.z);
    visual.root.rotation.y = p.yaw + obstacle.visualYaw; // pooled visuals keep their last rotation
    visual.root.scaling.setAll(obstacle.definition.visualScale ?? 1); // … and their last scale
    this.visuals.set(obstacle.uid, visual);
  }

  private hide(obstacle: ObstacleInstance): void {
    const visual = this.visuals.get(obstacle.uid);
    if (!visual) return;
    this.pool.release(visual);
    this.visuals.delete(obstacle.uid);
  }
}
